import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Axios from '@/shared/api/axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDonate, faFlag, faBan, faLock, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import Loading from '@/components/ui/Loading';
import FormattedText from '@/components/shared/FormattedText';
import { clearIncomingMessage, clearTypingIdolId, fetchRoomList, setCurrentChatIdolId, messageSent, roomRead, roomWebhookPausedChanged } from '@/shared/store/slices/chatSlice';

import placeholder_portrait_55x55 from "@/assets/imgs/placeholders/portrait_55x55.png";
import SafeImg from '@/components/shared/SafeImg';
import IdolAvatar from '@/components/ui/IdolAvatar';
import IdolName from '@/components/ui/IdolName';
import SuspendedMark from '@/components/ui/SuspendedMark';
import { useLocalMidnightTick } from '@/hooks/useLocalMidnightTick';
import {
    formatChatTime,
    formatChatDayLabel,
    formatRoomTime,
    isSameLocalDay,
    toBackendDateTimeString,
} from '@/shared/utils/formatDate';

import {
    Form,
    Dropdown,
    Button,
} from 'react-bootstrap';
import IconButton from '@/components/ui/IconButton';
import EllipsisToggle from '@/components/ui/EllipsisToggle';
import MainNav from '@/components/layout/AppNavbar';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import DonateModal from '@/components/ui/DonateModal';
import ReportModal from '@/components/ui/ReportModal';
import LoadingButton from '@/components/ui/LoadingButton';
import SubscriptionPlanModal from '@/features/subscription/components/SubscriptionPlanModal';
import SubscriptionConfirmModal from '@/features/subscription/components/SubscriptionConfirmModal';
import ChatMediaAttachment from '@/features/chat/components/ChatMediaAttachment';
import ChatMediaMessage from '@/features/chat/components/ChatMediaMessage';
import ChatLinkPreview, { detectLink } from '@/features/chat/components/ChatLinkPreview';
import { useStreamPolling, extractChatUnreadyVideos } from '@/hooks/useStreamPolling';
import { parseBackendDate } from '@/shared/utils/parseBackendDate';
import { isImeComposingKey } from '@/shared/utils/imeKeyboard';

function Chats() {
    // 1. 通用狀態和工具
    const { t } = useTranslation();
    // 聊天室列表的「今天 HH:mm / 昨天」與訊息的跨日分隔線都是純函式算的，沒有自己的計時器；
    // 頁面開著跨過午夜時統一重繪一次，避免昨天的訊息看起來像剛傳的
    useLocalMidnightTick();
    const location = useLocation();
    const navigate = useNavigate();
    const { idolUuid: urlIdolUuid } = useParams();
    const mainMessageBox = useRef(null);
    const messagesContentRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    const errorModal = useRef(null);
    const attachmentRef = useRef(null);
    const typingThrottleRef = useRef(null);
    const donateModalRef = useRef(null);
    const modalDonateSuccess = useRef(null);
    const modalSubscribeSuccess = useRef(null);
    const statusModalRef = useRef(null);
    const reportModalRef = useRef(null);
    const blockModalRef = useRef(null);
    const blockConfirmModalRef = useRef(null);
    const dispatch = useDispatch();
    const chatRooms = useSelector((state) => state.chat.rooms);
    const chatImageUrl = useSelector((state) => state.chat.imageUrl);
    const chatLoading = useSelector((state) => state.chat.loading);
    const chatError = useSelector((state) => state.chat.error);
    const roomsSynced = useSelector((state) => state.chat.roomsSynced);
    const incomingMessage = useSelector((state) => state.chat.incomingMessage);
    const typingIdolId = useSelector((state) => state.chat.typingIdolId);
    // WS 重連訊號（見 CLAUDE.md「WebSocket 重連與資料補拉」）
    const reconnectTick = useSelector((state) => state.app.reconnectTick);

    // 狀態碼處理
    const [currentStatusCode, setCurrentStatusCode] = useState('');

    // 2. 使用者相關狀態
    const idolId = useSelector((state) => state.user.idolId);
    const userId = useSelector((state) => state.user.userId);

    // 3. 聊天相關狀態
    const [isLoading, setIsLoading] = useState(false);
    const [chatHistory, setChatHistory] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [totalPages, setTotalPages] = useState(null);

    // 4. 接收者相關狀態
    const [receiverIdolInfo, setReceiverIdolInfo] = useState(null);
    const [receiverIdolId, setReceiverIdolId] = useState(null);
    const [receiverUserId, setReceiverUserId] = useState(null);

    // 訊息請求的防亂序：
    // - activeReceiverIdRef：目前聊天對象（render 時同步）。回應回來時對象已切換或已離開聊天室就丟棄，
    //   否則較慢的舊聊天室回應會蓋掉新聊天室的訊息與標題
    // - pageOneSeqRef：送出第 1 頁、套用第 1 頁、離開聊天室時都 +1，讓還沒回來的舊請求作廢。
    //   套用時要再 +1：WS 重連補拉第 1 頁的途中，使用者捲到頂送出的載入歷史是接在舊畫面上的，
    //   不擋的話第 1 頁回來後，它會把舊頁接在新的第 1 頁上（順序錯亂且之後重複）
    const activeReceiverIdRef = useRef(receiverIdolId);
    activeReceiverIdRef.current = receiverIdolId;
    const pageOneSeqRef = useRef(0);
    // 目前聊天室的 uuid（設定聊天對象時同步記下）。網址換成別的聊天室時（例如從鈴鐺通知點進來），
    // 新聊天室可能要等聊天室列表重抓回來才會設定，期間要先離開舊聊天室，否則舊聊天室晚到的回應會顯示出來
    const activeRoomUuidRef = useRef(null);
    // 載入歷史後要還原的捲動位置：等新訊息畫上去後（useLayoutEffect）才還原。
    // 舊寫法用 setTimeout(0)，WebKit 執行時 React 還沒畫完，scrollHeight 沒變，畫面停在最頂端
    const scrollRestoreRef = useRef(null);
    // 進入聊天頁後是否看過聊天室列表開始重抓：還沒看過就不能因為「列表裡找不到」判定 404。
    // 掛載時 ChatPage 的 effect 比 App 開始重抓列表的 effect 先執行，當下讀到的是 localStorage 裡的舊列表
    const hasSeenRoomListLoadRef = useRef(false);
    const [tipEnabled, setTipEnabled] = useState(false);

    // 訂閱流程 Modal 狀態（allow_message_status === 2 時的站內訂閱）
    const [showSubscriptionPlanModal, setShowSubscriptionPlanModal] = useState(false);
    const [showSubscriptionConfirmModal, setShowSubscriptionConfirmModal] = useState(false);
    const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState(null);
    // subscribe-idol 回應較慢，送出後鎖住確認 modal 並顯示 spinner
    const [isSubscribing, setIsSubscribing] = useState(false);
    // 聊天機器人自動回覆切換中（防連點）
    const [webhookToggling, setWebhookToggling] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);
    // 傳送時收到 CHAT_5007（已被對方封鎖）的接收者 id。記 id 而非布林值，只在當前聊天室為該接收者時生效；
    // 同一 session 內切回該聊天室仍會顯示，重傳成功（對方已解除封鎖）或重新載入頁面才解除
    const [unavailableReceiverId, setUnavailableReceiverId] = useState(null);
    // 讀取／傳送時收到 IDOL_3045（對方已停權）的接收者 id：聊天室列表還沒更新時（例如聊天途中對方才被停權）先在本地標記，
    // 同時重抓列表；列表回來後由 receiver_idol_is_suspended 接手
    const [suspendedReceiverId, setSuspendedReceiverId] = useState(null);
    // 直接開停權聊天室的網址：StatusModal 關閉後回到未選聊天室的 /chats（與偶像頁等「關閉後導頁」一致）。
    // 記被擋的聊天室 uuid，只有關閉時網址仍是它才導頁（modal 開著時按上一頁回到別的聊天室，不能把人踢出去）。
    // 只有擋網址進入時設定，聊天途中收到 IDOL_3045 仍留在原聊天室
    const [unavailableRoomUuid, setUnavailableRoomUuid] = useState(null);
    const leaveUnavailableRoom = () => {
        setUnavailableRoomUuid(null);
        navigate('/chats', { replace: true });
    };

    // 5. UI 相關狀態
    const [showScrollIcon, setShowScrollIcon] = useState(false);
    const [donatedAmount, setDonatedAmount] = useState(null);
    // 媒體附件陣列：[{ file, mediaType, previewUrl }]
    const [previewFiles, setPreviewFiles] = useState([]);
    const canSend = (messageText.trim() !== '' || previewFiles.length > 0) && !previewFiles.some(p => p.uploading);
    // 當前聊天室（來自 get-room-list，含 allow_message_status / webhook_paused）
    const currentRoom = chatRooms?.find(r => r.receiver_idol_id === receiverIdolId);
    // allow_message_status: 1 可私訊 / 2 未訂閱不可私訊 / 3 對方關閉聊天不可私訊 / 4 對方已停權（同 receiver_idol_is_suspended）
    const allowMessageStatus = currentRoom?.allow_message_status;
    const isReceiverUnavailable = receiverIdolId !== null && unavailableReceiverId === receiverIdolId;
    // 對方已停權：讀取、傳送訊息都會回 IDOL_3045，不抓訊息、輸入區改顯示停權提示
    const isReceiverSuspended = receiverIdolId !== null
        && (currentRoom?.receiver_idol_is_suspended || suspendedReceiverId === receiverIdolId);
    // 沒設定 webhook 的房間後端回 null／不回欄位，只有布林值才顯示切換按鈕
    const showWebhookToggle = typeof currentRoom?.webhook_paused === 'boolean';
    const [roomParams, setRoomParams] = useState({
        sender_idol_id: idolId,
        receiver_idol_id: receiverIdolId,
        limit: 50,
        page: 1
    });

    // 重置 textarea 高度的函數
    const resetTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.value = '';
            textarea.style.height = 'auto';
        }
    };

    // 生成臨時 ID
    const generateTempId = () => {
        return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    };

    // 依時間插入訊息，維持陣列為時間升冪
    const insertMessageInOrder = (messages, newMessage) => {
        if (!messages || messages.length === 0) {
            return [newMessage];
        }

        const newTime = parseBackendDate(newMessage.created_at);

        // 沒有時間、或時間解析不出來，就沒有可靠的排序依據。不編一個代用值
        // （舊版用 ?? 0，會把解析失敗的訊息排到 1970 年、也就是整串的最前面），
        // 一律附加到末尾——它就是剛剛才到的那一則。非空卻解析失敗代表後端違約，出聲
        if (newTime === null) {
            if (newMessage.created_at) {
                console.error('[chat] 訊息時間無法解析，改為附加到末尾：', newMessage.created_at);
            }
            return [...messages, newMessage];
        }

        // 從尾端往前找第一則「不晚於新訊息」的，插在它後面。
        // 舊版只檢查最後 5 則，超出範圍就無條件插在第 (len - 5) 個位置，不保證順序正確；
        // 這裡掃到底，實務上第一輪就會命中（新訊息通常就是最新的一則）
        for (let i = messages.length - 1; i >= 0; i--) {
            const msgTime = parseBackendDate(messages[i].created_at);
            // 既有訊息若無可比較的時間就跳過，不拿它當基準
            if (msgTime === null) continue;
            if (newTime >= msgTime) {
                const result = [...messages];
                result.splice(i + 1, 0, newMessage);
                return result;
            }
        }

        // 比所有可比較的訊息都早
        return [newMessage, ...messages];
    };

    // 1. 核心功能函數
    const getMessage = async () => {
        const requestReceiverId = roomParams.receiver_idol_id;
        const seq = ++pageOneSeqRef.current;
        const isStale = () => seq !== pageOneSeqRef.current || requestReceiverId !== activeReceiverIdRef.current;
        try {
            const response = await Axios.post('/chat/get-messages', {
                ...roomParams,
                page: 1
            });
            if (isStale()) return;
            if (response.data.status_code === 'SYSTEM_1000') {
                // 套用第 1 頁的當下作廢途中的載入歷史（見 pageOneSeqRef 註解）。只在成功時做：
                // 失敗時畫面沒被換掉，途中的載入歷史仍接在目前畫面上，照常套用
                pageOneSeqRef.current++;
                setChatHistory(response.data.data.reverse());
                setReceiverIdolInfo(response.data.friend_idol);
                setTotalPages(Math.ceil(response.data.total / roomParams.limit));
                setTipEnabled(response.data.friend_idol.tip_enabled);
                // 畫面已換回第 1 頁，分頁也要歸 1：WS 重連補拉時若之前已載入過歷史，
                // 不歸 1 的話往上捲會從舊頁碼的下一頁接續，中間幾頁無聲消失
                setRoomParams(prev => ({ ...prev, page: 1 }));

                // 後端讀取訊息即標記已讀，同步清掉列表上的未讀數
                dispatch(roomRead(requestReceiverId));
                // 讀得到訊息代表對方已解除停權：收起本地的停權標記
                setSuspendedReceiverId(prev => (prev === requestReceiverId ? null : prev));
            } else {
                handleChatFailStatus(response.data.status_code, requestReceiverId);
            }
        } catch (error) {
            if (isStale()) return;
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Get messages error: ', error);
            }
        }
    };

    // 修改 handleRoomClick
    const handleRoomClick = (clickedIdolId, clickedUserId, clickedIdolUuid) => {
        // 如果點擊的是當前已選中的聊天室，不做任何操作
        if (clickedIdolId === receiverIdolId) {
            return;
        }

        setRoomParams(prev => ({
            ...prev,
            receiver_idol_id: clickedIdolId,
            page: 1
        }));
        setReceiverIdolId(clickedIdolId);
        setReceiverUserId(clickedUserId);
        activeRoomUuidRef.current = clickedIdolUuid;
        setChatHistory(null);
        setPreviewFiles(prev => {
            prev.forEach(p => { if (p.videoUrl) URL.revokeObjectURL(p.videoUrl); });
            return [];
        });

        // 更新 URL
        if (clickedIdolUuid) {
            navigate(`/chats/${clickedIdolUuid}`, { replace: true });
        }

        // 只有當訊息內容有變化時才重置
        if (messageText !== '') {
            setMessageText('');
            // 使用 setTimeout 确保 DOM 已更新
            setTimeout(resetTextareaHeight, 0);
        }
    };

    // 更新訊息狀態（用於樂觀更新後的狀態變更）
    const updateMessageStatus = (tempId, status, serverData = null) => {
        // 檢查放在 setState 之外：updater 必須是純函式，React 可能重複呼叫或丟棄它
        // （StrictMode 目前在 main.jsx 被註解掉，但不該依賴這件事）
        if (serverData && !serverData.created_at) {
            console.error('[chat] 送出成功但伺服器未回傳 created_at，暫用本地時間戳');
        }
        setChatHistory(prev => {
            if (!prev) return prev;
            return prev.map(msg => {
                if (msg.tempId === tempId) {
                    if (serverData) {
                        // 成功時更新為伺服器資料。保留本地時間戳不是「編一個代用值」——
                        // 它是 getFormattedTime() 產生的真值，與後端同格式同時區
                        return {
                            ...msg,
                            status,
                            created_at: serverData.created_at || msg.created_at
                        };
                    }
                    return { ...msg, status };
                }
                return msg;
            });
        });
    };

    // 聊天 API（傳送訊息、讀取訊息）的非成功狀態碼處理。CHAT_5007（已被對方封鎖）不跳 StatusModal，
    // 改由輸入區顯示中性的「無法傳送」提示，避免被封鎖方得知自己被封鎖。
    // 讀取訊息是否會回 CHAT_5007 未經後端確認，一併走這裡以免洩漏封鎖
    // （只用到 setter 與 ref，以 useCallback 固定參考，供 loadMoreMessages 列為依賴）
    // IDOL_3045（對方已停權）照常跳 StatusModal，另外標記該接收者並重抓聊天室列表，更新列表上的停權標記
    const handleChatFailStatus = useCallback((statusCode, targetReceiverId) => {
        if (statusCode === 'CHAT_5007') {
            setUnavailableReceiverId(targetReceiverId);
            return;
        }
        if (statusCode === 'IDOL_3045') {
            setSuspendedReceiverId(targetReceiverId);
            dispatch(fetchRoomList());
        }
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
    }, [dispatch]);

    // 重傳失敗訊息
    const handleRetry = async (tempId) => {
        const failedMessage = chatHistory?.find(msg => msg.tempId === tempId);
        if (!failedMessage) return;

        // 更新狀態為 sending
        updateMessageStatus(tempId, 'sending');

        const messagePayload = {
            sender_idol_id: idolId,
            receiver_idol_id: receiverIdolId,
            content: failedMessage.message,
        };

        try {
            const res = await Axios.post("/chat/send-message", messagePayload);

            if (res.data.status_code === 'SYSTEM_1000') {
                updateMessageStatus(tempId, 'sent', res.data.data);
                // 對方已解除封鎖：收起 CHAT_5007 提示列，恢復輸入區
                setUnavailableReceiverId(prev => (prev === messagePayload.receiver_idol_id ? null : prev));
                // 更新聊天室列表
                const currentTime = getFormattedTime();
                updateChatRoomsList(currentTime, failedMessage.message);
            } else {
                updateMessageStatus(tempId, 'failed');
                handleChatFailStatus(res.data.status_code, messagePayload.receiver_idol_id);
            }
        } catch (error) {
            updateMessageStatus(tempId, 'failed');
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Retry message error: ', error);
            }
        }
    };

    // 刪除失敗訊息
    const handleDeleteFailed = (tempId) => {
        setChatHistory(prev => {
            if (!prev) return prev;
            return prev.filter(msg => msg.tempId !== tempId);
        });
    };

    // 樂觀更新用的時間戳，格式與後端一致（UTC，YYYY-MM-DD HH:mm:ss）。
    //
    // 這個字串不只拿來顯示——insertMessageInOrder 用它排序、送出後也用它跟 server 回傳的
    // created_at 比對找出對應訊息（見 handleSubmit），所以必須與後端同格式同時區，
    // 不能因為畫面改顯示本地時間就改成本地字串。顯示端一律再經 formatChatTime 轉本地。
    const getFormattedTime = () => toBackendDateTimeString(new Date());

    const handleSubmit = async () => {
        const hasText = messageText.trim();
        const hasMedia = previewFiles.length > 0;
        if (!hasText && !hasMedia) return;
        if (previewFiles.some(p => p.uploading)) return;

        const tempId = generateTempId();
        const currentMessage = messageText;
        const currentPreviews = previewFiles;
        const formattedTime = getFormattedTime();

        // 1. 立即清空輸入框與預覽
        setMessageText('');
        resetTextareaHeight();
        if (hasMedia) {
            currentPreviews.forEach(p => { if (p.videoUrl) URL.revokeObjectURL(p.videoUrl); });
            setPreviewFiles([]);
        }

        // 2. 樂觀更新：立即顯示訊息（status: 'sending'）
        const optimisticMessage = {
            tempId,
            status: 'sending',
            created_at: formattedTime,
            message: currentMessage,
            media: hasMedia
                ? currentPreviews.map(p =>
                    p.mediaType === 'image'
                        ? { type: 'image', file_url: p.previewUrl, is_expired: false }
                        : { type: 'video', stream_status: 'processing', is_expired: false }
                )
                : null,
            sender_idol_id: idolId,
            receiver_idol_id: receiverIdolId,
            is_tip_message: false,
            tip_amount: null,
        };

        setChatHistory(prev => prev ? [...prev, optimisticMessage] : [optimisticMessage]);
        handleScrollToBottom(true);

        // 3. 非同步發送 API
        let payload;

        if (hasMedia) {
            const formData = new FormData();
            formData.append('sender_idol_id', idolId);
            formData.append('receiver_idol_id', receiverIdolId);
            if (currentMessage) formData.append('content', currentMessage);
            currentPreviews.forEach(p => {
                if (p.mediaType === 'image') formData.append('images[]', p.file);
            });
            currentPreviews.forEach(p => {
                if (p.mediaType === 'video') formData.append('stream_uids[]', p.uid);
            });
            payload = formData;
        } else {
            payload = {
                sender_idol_id: idolId,
                receiver_idol_id: receiverIdolId,
                content: currentMessage,
            };
        }

        try {
            const res = await Axios.post("/chat/send-message", payload);

            if (res.data.status_code === 'SYSTEM_1000') {
                updateMessageStatus(tempId, 'sent', res.data.data);
                updateChatRoomsList(formattedTime, currentMessage, hasMedia ? 2 : 1);

                const hasVideo = hasMedia && currentPreviews.some(p => p.mediaType === 'video');
                if (hasVideo) {
                    const sentCreatedAt = res.data.data?.created_at;
                    const refreshRes = await Axios.post('/chat/get-messages', { ...roomParams, page: 1 });
                    if (refreshRes.data.status_code === 'SYSTEM_1000' && sentCreatedAt) {
                        // 這是整條樂觀更新鏈上唯一的字串等值比對，成立前提是本地產生的時間戳
                        // 與後端同格式同時區（見 getFormattedTime）。若哪天把它改成本地字串，
                        // 這裡會靜默失效——找不到 serverMsg，影片訊息就永遠停在處理中
                        const serverMsg = refreshRes.data.data.find(m => m.created_at === sentCreatedAt && m.sender_idol_id === idolId);
                        if (serverMsg) {
                            setChatHistory(prev => prev?.map(msg =>
                                msg.tempId === tempId ? { ...serverMsg, tempId, status: 'sent' } : msg
                            ));
                        }
                    }
                }
            } else {
                updateMessageStatus(tempId, 'failed');
                handleChatFailStatus(res.data.status_code, optimisticMessage.receiver_idol_id);
            }
        } catch (error) {
            updateMessageStatus(tempId, 'failed');
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal。
            // 改讀攔截器附加的 httpErrorCode：413 等 HTTP 錯誤的 body 沒有 status_code，
            // 原本只讀 error.response.data.status_code 會讓「檔案過大」永遠顯示不出來。
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Send message error: ', error);
            }
        }
    };

    // 3. 輔助函數
    const updateChatRoomsList = (currentTime, messageContent = messageText, messageType = 1) => {
        dispatch(messageSent({
            receiverIdolId,
            receiverUserId,
            receiverIdol: receiverIdolInfo,
            message: messageContent,
            messageType,
            createdAt: currentTime,
        }));
    };

    // 4. 滾動相關函數
    const handleNewMessages = useCallback((newMessages) => {
        setChatHistory(prev => [...newMessages.reverse(), ...prev]);
        setRoomParams(prev => ({ ...prev, page: prev.page + 1 }));
    }, []);

    const loadMoreMessages = useCallback(async () => {
        const requestReceiverId = roomParams.receiver_idol_id;
        const seq = pageOneSeqRef.current;
        const isStale = () => seq !== pageOneSeqRef.current || requestReceiverId !== activeReceiverIdRef.current;
        setIsLoading(true);
        try {
            const response = await Axios.post('/chat/get-messages', {
                ...roomParams,
                page: roomParams.page + 1
            });
            if (isStale()) return;

            if (response.data.status_code === 'SYSTEM_1000') {
                // 記下目前位置，新訊息畫上去後由 useLayoutEffect 還原（見 scrollRestoreRef 註解）
                const box = mainMessageBox.current;
                scrollRestoreRef.current = { prevHeight: box.scrollHeight, prevTop: box.scrollTop };
                handleNewMessages(response.data.data);
            } else {
                handleChatFailStatus(response.data.status_code, requestReceiverId);
            }
        } catch (error) {
            if (isStale()) return;
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Load more messages error: ', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [roomParams, handleNewMessages, handleChatFailStatus]);

    const handleScrollLoadHistory = useCallback(async () => {
        const { scrollTop, scrollHeight, clientHeight } = mainMessageBox.current;
        const hasScrollbar = scrollHeight > clientHeight;

        // <= 0：WebKit 捲到頂的回彈（elastic overscroll）期間 scrollTop 會是負數
        if (!isLoading &&
            roomParams.page < totalPages &&
            scrollTop <= 0 &&
            chatHistory?.length > 0 &&
            hasScrollbar) {
            await loadMoreMessages();
        }
    }, [isLoading, roomParams.page, totalPages, chatHistory, loadMoreMessages]);

    // 捲動監聽只綁一次，計時器到期時透過 ref 呼叫最新的處理函式。
    // 舊寫法每次 handleScrollLoadHistory 改變就重綁，但前一個 listener 排定的計時器沒清：
    // 到期時呼叫的是舊 closure（isLoading、頁碼是舊值，可能誤判不載入，而已在頂端不會再有捲動事件），
    // 離開聊天頁後才到期還會讀到 null 的 mainMessageBox
    const scrollHandlerRef = useRef(null);
    scrollHandlerRef.current = () => {
        handleScrollIcon();
        handleScrollLoadHistory();
    };

    useEffect(() => {
        const messageBox = mainMessageBox.current;
        let timer = null;
        // 節流：第一次捲動後 500ms 才處理，處理時讀當下的捲動位置，期間的捲動事件不另外排程
        const handleScroll = () => {
            if (timer) return;
            timer = setTimeout(() => {
                timer = null;
                scrollHandlerRef.current();
            }, 500);
        };

        messageBox.addEventListener('scroll', handleScroll);
        return () => {
            messageBox.removeEventListener('scroll', handleScroll);
            clearTimeout(timer);
        };
    }, []);

    // 載入歷史後還原捲動位置：在瀏覽器繪製前執行，此時新訊息已在 DOM 上
    useLayoutEffect(() => {
        if (!scrollRestoreRef.current) return;
        const { prevHeight, prevTop } = scrollRestoreRef.current;
        scrollRestoreRef.current = null;
        const box = mainMessageBox.current;
        box.scrollTop = box.scrollHeight - prevHeight + prevTop;
    }, [chatHistory]);

    // 網址不再指向目前的聊天室時，離開目前的聊天室：
    // - urlIdolUuid 消失（如封鎖後導向 /chats）
    // - 換成別的聊天室（如從鈴鐺通知點進來）：新聊天室由下方 effect 在聊天室列表回來後設定
    // 點聊天室列表、從偶像頁進來時，會先設定聊天對象與 activeRoomUuidRef 再改網址，兩者一致不會觸發
    useEffect(() => {
        if (urlIdolUuid && urlIdolUuid === activeRoomUuidRef.current) return;
        // 同步作廢途中的訊息請求：這裡的 setState 要等下一次 render 才更新 activeReceiverIdRef，
        // 這段空窗內回來的舊回應仍會通過對象比對（見 pageOneSeqRef 註解）
        pageOneSeqRef.current++;
        activeRoomUuidRef.current = null;
        setReceiverIdolId(null);
        setReceiverIdolInfo(null);
        setReceiverUserId(null);
        setChatHistory(null);
        setTotalPages(null);
        setRoomParams(prev => ({ ...prev, receiver_idol_id: null, page: 1 }));
    }, [urlIdolUuid]);

    useEffect(() => {
        if (chatLoading) {
            hasSeenRoomListLoadRef.current = true;
        }
    }, [chatLoading]);

    // 離開聊天頁：作廢還沒回來的訊息請求，回來時不再讀已卸載的 mainMessageBox
    // （cleanup 要讀的正是當下最新的序號，持有 ref 物件本身即可）
    useEffect(() => {
        const seqRef = pageOneSeqRef;
        return () => {
            seqRef.current++;
        };
    }, []);

    useEffect(() => {
        // 使用 Redux 中的 chatRooms（由 App.jsx 在頁面切換時更新）
        const rooms = chatRooms;

        // 從 idol send message 進入（使用 location.state）- 優先處理
        if (location.state && location.state.receiverIdolInfo && rooms) {
            const stateReceiverIdolId = parseInt(location.state.receiverIdolInfo.idol_id);
            const stateReceiverUserId = parseInt(location.state.receiverIdolInfo.user_id);
            const stateReceiverIdolUuid = location.state.receiverIdolInfo.uuid;

            // 檢查該接收者是否在聊天室列表中
            const existingRoom = rooms.find(room => room.receiver_idol_id === stateReceiverIdolId);

            setReceiverIdolId(stateReceiverIdolId);
            setReceiverUserId(stateReceiverUserId);
            setReceiverIdolInfo(location.state.receiverIdolInfo);
            activeRoomUuidRef.current = stateReceiverIdolUuid;

            if (existingRoom) {
                setRoomParams(prev => ({
                    ...prev,
                    receiver_idol_id: stateReceiverIdolId,
                }));
            } else {
                // 如果不在聊天列表中，設置空的聊天記錄
                setRoomParams(prev => ({
                    ...prev,
                    receiver_idol_id: stateReceiverIdolId,
                }));
            }

            // 更新 URL 為帶有 uuid 的路徑，並清除 state
            if (stateReceiverIdolUuid) {
                navigate(`/chats/${stateReceiverIdolUuid}`, { replace: true, state: {} });
            } else {
                navigate(location.pathname, { replace: true, state: {} });
            }
            return;
        }

        // 處理 URL 參數（從 URL 直接進入，沒有 location.state）
        // 本次 session 還沒抓過列表時，rooms 是 localStorage 的舊列表（或 null）：後端可能已沒有該聊天室，
        // 不拿它選聊天室也不拿它判斷 404，等列表重抓回來再處理；重抓失敗仍要顯示錯誤
        if (urlIdolUuid) {
            const targetRoom = roomsSynced ? rooms.find(room => room.receiver_idol.uuid === urlIdolUuid) : undefined;
            if (targetRoom?.receiver_idol_is_suspended && targetRoom.receiver_idol_id !== receiverIdolId) {
                // 對方已停權的聊天室不能進入（列表上也不能點）：不選聊天室，提示關閉後回到 /chats；
                // modal 被全站鎖略過（show() 回 false）時直接回 /chats。列表更新使 effect 重跑時重複 show() 沒有副作用。
                // 已經在這間聊天室裡（聊天途中對方才被停權、列表重抓回來）不踢出，由輸入區的停權提示處理
                setUnavailableRoomUuid(urlIdolUuid);
                setCurrentStatusCode('IDOL_3045');
                if (statusModalRef.current && !statusModalRef.current.show()) leaveUnavailableRoom();
            } else if (targetRoom) {
                setReceiverIdolId(targetRoom.receiver_idol_id);
                setReceiverUserId(targetRoom.receiver_user_id);
                activeRoomUuidRef.current = urlIdolUuid;
                setRoomParams(prev => ({
                    ...prev,
                    receiver_idol_id: targetRoom.receiver_idol_id,
                }));
            } else if (hasSeenRoomListLoadRef.current && !chatLoading && !receiverIdolId && chatError === null && roomsSynced) {
                // 只有在進入聊天頁後的列表重抓成功後，確認找不到且 receiverIdolId 尚未設定才跳轉 404
                // 避免從偶像頁面進入時因 chatRooms 更新而誤導向，也避免拿 localStorage 的舊列表判斷。
                // 重抓失敗時無法判斷聊天室是否存在，不導 404，錯誤由下方 effect 顯示
                navigate('/404', { replace: true });
            }
        }
    }, [chatRooms, chatLoading, roomsSynced]);

    // 直接開聊天室網址、列表重抓失敗：顯示錯誤。只在重抓結束（chatLoading 變 false）時判斷，
    // 同一次失敗只顯示一次；放在上方 effect 會隨 WS 訊息改變列表重跑，關掉 modal 後又跳出。
    // 只依賴 chatLoading：chatError 與它在同一個 action 更新，結束那次 render 讀到的就是這次的結果
    useEffect(() => {
        if (!urlIdolUuid || receiverIdolId || chatLoading || !hasSeenRoomListLoadRef.current) return;
        // 列表重抓失敗時 error 不是 null（非 HTTP 例外、回應沒有 status_code 時是 undefined，已由 fetchRoomList 印出）
        // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
        if (chatError && chatError !== 'NETWORK_ERROR') {
            setCurrentStatusCode(chatError);
            statusModalRef.current?.show();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatLoading]);

    useEffect(() => {
        if (receiverIdolId && roomParams.receiver_idol_id) {
            // 只有在聊天室列表中存在該接收者時才取得訊息
            const existingRoom = chatRooms?.find(room => room.receiver_idol_id === receiverIdolId);
            if (existingRoom) {
                // 標頭先用聊天室列表上的對方資料，get-messages 成功後再由 friend_idol 覆蓋。
                // 讀取失敗（例如 IDOL_3045）或對方已停權不抓訊息時，標頭才不會停在前一個聊天室的對象
                setReceiverIdolInfo(existingRoom.receiver_idol);
                // 對方已停權：get-messages 會回 IDOL_3045，不抓訊息
                if (!existingRoom.receiver_idol_is_suspended) {
                    getMessage();
                }
            }
            handleScrollToBottom();
            handleScrollIcon();
        }
    }, [receiverIdolId, roomParams.receiver_idol_id]);

    useEffect(() => {
        if (chatHistory && roomParams.page === 1) {
            handleScrollToBottom();
        }
    }, [chatHistory]);

    // 監聽訊息容器與聊天框高度變化，有變化且在底部時自動 scroll
    useEffect(() => {
        const content = messagesContentRef.current;
        const box = mainMessageBox.current;
        if (!content || !box) return;

        const observer = new ResizeObserver(() => {
            if (shouldAutoScrollRef.current) {
                box.scrollTop = box.scrollHeight;
            }
        });

        observer.observe(content);
        observer.observe(box);
        return () => observer.disconnect();
    }, []);

    // 聊天室影片狀態輪詢：有未 ready 的影片時定期更新 stream_status
    const { videos: unreadyChatVideos, createdAt: chatStreamCreatedAt } = useMemo(
        () => extractChatUnreadyVideos(chatHistory),
        [chatHistory]
    );
    useStreamPolling({
        endpoint: '/chat/get-stream-status',
        unreadyVideos: unreadyChatVideos,
        createdAt: chatStreamCreatedAt,
        onStatusChange: (changedItems) => {
            setChatHistory(prev => {
                if (!prev) return prev;
                return prev.map(msg => {
                    if (!msg.media) return msg;
                    let updated = false;
                    const newMedia = msg.media.map(item => {
                        const change = changedItems.find(c => c.stream_uid === item.stream_uid);
                        if (change) {
                            updated = true;
                            return { ...item, stream_status: change.stream_status };
                        }
                        return item;
                    });
                    return updated ? { ...msg, media: newMedia } : msg;
                });
            });
        },
    });

    // 添加滾動事件監聽
    const handleScrollToBottom = (force = false) => {
        if (force || chatHistory) {
            mainMessageBox.current.scrollTop = mainMessageBox.current.scrollHeight;
            shouldAutoScrollRef.current = true;
        }
    }

    const handleScrollIcon = () => {
        if (!mainMessageBox.current) return;
        const { scrollTop, scrollHeight, clientHeight } = mainMessageBox.current;
        const distFromBottom = scrollHeight - scrollTop - clientHeight;
        setShowScrollIcon(distFromBottom > 500);
        shouldAutoScrollRef.current = distFromBottom < 50;
    }



    // 只有這次捲動要平滑：直接指定 behavior，不去改容器的 scroll-behavior style
    // （舊寫法靠 scrollend 事件還原 style，不支援 scrollend 的 Safari 會一直留著 smooth，
    // 之後載入歷史訊息時的位置還原就變成動畫捲動）
    const scrollToBottom = () => {
        const box = mainMessageBox.current;
        box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
    }

    // 訊息輸入處理（加入 throttle 發送 typing API）
    const handleMessageInput = (e) => {
        setMessageText(e.target.value);

        // Throttle 3 秒發送 typing API（避免過多請求）
        if (!typingThrottleRef.current && receiverIdolId) {
            // 立即發送。typing 是輸入的附帶通知、不是使用者要送出的操作，
            // 標記為背景請求：斷線時不觸發整頁 ServerErrorPage，避免洗掉輸入框裡的文字
            Axios.post('/chat/typing', {
                sender_idol_id: idolId,
                receiver_idol_id: receiverIdolId,
            }, { isBackground: true }).catch(() => {
                // 靜默失敗，不影響使用體驗
            });

            // 設定 3 秒冷卻
            typingThrottleRef.current = setTimeout(() => {
                typingThrottleRef.current = null;
            }, 3000);
        }
    };

    // 自動調整 textarea 高度
    const autoResizeTextarea = (e) => {
        const textarea = e.target;
        const maxHeight = 250;

        textarea.style.height = 'auto';
        // 計算實際內容高度，但不超過最大高度
        const scrollHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = scrollHeight + 'px';

        handleScrollToBottom();
    };

    // 在現有的 refs 聲明中添加
    const textareaRef = useRef(null);

    // 添加一個保持焦點的函數
    const maintainFocus = useCallback(() => {
        if (textareaRef.current && document.activeElement !== textareaRef.current) {
            textareaRef.current.focus();
        }
    }, []);

    // 在 typingIdolId 變化時保持焦點並滾動到底部
    useEffect(() => {
        if (receiverIdolId) {
            maintainFocus();
        }
        // 當對方開始輸入時，如果用戶在底部附近，自動滾動到底
        if (typingIdolId && typingIdolId === receiverIdolId && mainMessageBox.current) {
            const messageBox = mainMessageBox.current;
            const { scrollTop, scrollHeight, clientHeight } = messageBox;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 200;
            if (isNearBottom) {
                // 使用 setTimeout 確保「正在輸入」DOM 已渲染
                setTimeout(() => {
                    if (mainMessageBox.current) {
                        mainMessageBox.current.scrollTop = mainMessageBox.current.scrollHeight;
                    }
                }, 0);
            }
        }
    }, [typingIdolId, maintainFocus, receiverIdolId]);

    // 收到 WS 訊息時自動觸發，使用者沒有主動操作：標記為背景請求，斷線時不觸發整頁 ServerErrorPage
    const updateReadStatus = async () => {
        try {
            await Axios.post('/chat/get-messages', {
                ...roomParams,
                page: 1
            }, { isBackground: true });
        } catch (error) {
            console.error('更新未讀狀態失敗:', error);
        }
    };

    // 監聽 Redux incomingMessage（由 App.jsx 的 MessageSent 處理後 dispatch）
    useEffect(() => {
        if (!incomingMessage) return;

        // 確認是當前聊天對象的訊息
        if (Number(incomingMessage.sender_idol_id) === receiverIdolId) {
            // 更新聊天記錄（使用尾部檢查插入，避免全量排序）
            setChatHistory(prev => {
                const newMessage = {
                    created_at: incomingMessage.created_at,
                    message: incomingMessage.message,
                    media: incomingMessage.media ?? null,
                    sender_idol_id: Number(incomingMessage.sender_idol_id),
                    receiver_idol_id: incomingMessage.receiver_idol_id,
                    is_tip_message: incomingMessage.is_tip_message,
                    tip_amount: incomingMessage.tip_amount
                };
                return insertMessageInOrder(prev, newMessage);
            });

            // 更新未讀狀態
            updateReadStatus();

            // 清除對方正在輸入狀態（因為已收到訊息）
            dispatch(clearTypingIdolId());

            handleScrollToBottom(true);
        }

        // 清除 incomingMessage
        dispatch(clearIncomingMessage());
    }, [incomingMessage, receiverIdolId, dispatch]);

    // 設置當前聊天對象（App 的 WS 訊息 handler 據此判斷）
    useEffect(() => {
        dispatch(setCurrentChatIdolId(receiverIdolId));
        return () => {
            dispatch(setCurrentChatIdolId(null));
        };
    }, [receiverIdolId, dispatch]);

    // WS 重連後補拉當前聊天室訊息（斷線期間的推播會漏掉，要主動拉一次最新 page 1）
    // 初始 reconnectTick 是 0，pusher 第一次 connect 才會跳到 1，所以不會在初次掛載多打一次 API
    useEffect(() => {
        if (reconnectTick > 0 && receiverIdolId && roomParams.receiver_idol_id && !isReceiverSuspended) {
            if (import.meta.env.DEV) console.log('[ChatPage] reconnect → refetch messages, tick:', reconnectTick);
            getMessage();
        }
    }, [reconnectTick]); // eslint-disable-line react-hooks/exhaustive-deps


    // 封鎖偶像：送出後鎖住確認 modal，回應後才關閉並顯示結果
    const blockIdol = async () => {
        setIsBlocking(true);
        try {
            const res = await Axios.post("/idol/create-block", {
                idol_id: idolId,
                block_idol_id: receiverIdolId
            });
            switch (res.data.status_code) {
                case 'SYSTEM_1000':
                    blockModalRef.current?.show();
                    break;
                default:
                    setCurrentStatusCode(res.data.status_code);
                    statusModalRef.current?.show();
                    break;
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsBlocking(false);
            blockConfirmModalRef.current?.hide();
        }
    };

    // Donate 打賞按鈕
    const handleDonate = useCallback(() => {
        donateModalRef.current?.show();
    }, []);

    const handleDonateConfirm = useCallback(async (amount, message) => {
        if (!amount || !message.trim()) return;

        const tipMessage = {
            sender_idol_id: idolId,
            receiver_idol_id: receiverIdolId,
            content: message,
            tip_amount: parseFloat(amount)
        };

        try {
            const res = await Axios.post("/chat/send-message", tipMessage);
            if (res.data.status_code === 'SYSTEM_1000') {
                const formattedTime = getFormattedTime();

                // 更新聊天記錄（直接 append，打賞訊息一定是最新的）
                setChatHistory(prev => {
                    const newMessage = {
                        created_at: formattedTime,
                        message: message,
                        sender_idol_id: idolId,
                        receiver_idol_id: receiverIdolId,
                        is_tip_message: true,
                        // 用後端回傳的實際扣款金額（後端會四捨五入到兩位），不用使用者輸入值
                        tip_amount: res.data.data.tip_amount
                    };
                    return prev ? [...prev, newMessage] : [newMessage];
                });

                // 更新聊天室列表
                updateChatRoomsList(formattedTime, message);

                // 滾動到底部
                handleScrollToBottom(true);

                setDonatedAmount(res.data.data.tip_amount);
                modalDonateSuccess.current?.show();
            } else {
                // 處理非成功的狀態碼
                handleChatFailStatus(res.data.status_code, tipMessage.receiver_idol_id);
            }
        } catch (error) {
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            } else if (!error.httpErrorCode) {
                console.error('Donate error: ', error);
                errorModal.current?.show();
            }
        }
    }, [idolId, receiverIdolId, updateChatRoomsList, handleScrollToBottom, handleChatFailStatus]);

    // 選擇訂閱方案
    const handleSubscriptionPlanConfirm = useCallback((plan) => {
        setSelectedSubscriptionPlan(plan);
        setShowSubscriptionPlanModal(false);
        setShowSubscriptionConfirmModal(true);
    }, []);

    // 錢包餘額抓不到就關掉扣款 modal 並顯示錯誤碼：餘額未知時不該讓使用者繼續走扣款流程
    const handleSubscribeWalletError = useCallback((statusCode) => {
        setShowSubscriptionConfirmModal(false);
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
    }, []);

    // 確認訂閱，成功後重抓聊天室列表讓輸入框恢復
    const handleSubscribeConfirm = async () => {
        setIsSubscribing(true);
        try {
            const res = await Axios.post('/idol/subscribe-idol', {
                idol_id: idolId,
                view_idol_id: receiverIdolId,
                plan_duration: selectedSubscriptionPlan.duration
            });

            setShowSubscriptionConfirmModal(false);
            if (res.data.status_code === 'SYSTEM_1000') {
                modalSubscribeSuccess.current?.show();
                dispatch(fetchRoomList());
            } else {
                setCurrentStatusCode(res.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            console.error('Subscribe error: ', error);
            setShowSubscriptionConfirmModal(false);
            // NETWORK_ERROR 已由 axios 攔截器觸發全域錯誤畫面，不再重複彈窗
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsSubscribing(false);
        }
    };

    // 切換聊天機器人自動回覆（webhook 暫停/恢復）
    const handleToggleWebhookPause = async () => {
        setWebhookToggling(true);
        try {
            const res = await Axios.post('/chat/toggle-webhook-pause', {
                idol_id: idolId,
                chat_with_idol_id: receiverIdolId
            });

            if (res.data.status_code === 'SYSTEM_1000') {
                dispatch(roomWebhookPausedChanged({
                    receiverIdolId,
                    webhookPaused: res.data.webhook_paused,
                }));
            } else {
                setCurrentStatusCode(res.data.status_code);
                statusModalRef.current?.show();
            }
        } catch (error) {
            console.error('Toggle webhook pause error: ', error);
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setWebhookToggling(false);
        }
    };

    return (
        <>
            <MainNav />
            <main className="container-lg nav-padding">
                <div className="message-wrap bg-white d-flex flex-column flex-md-row">
                    <div className="sidebar">
                        <div className="sidebar-title d-flex align-items-center ps-2 py-3">
                            <h4 className="mb-0">{t('message')}</h4>
                        </div>
                        <div className="box">
                            {/* 聊天室列表 */}
                            {chatRooms && chatRooms.length > 0 ? (
                                chatRooms.map((room) => (
                                    <div key={room.receiver_idol_id} className={`${room.receiver_idol_is_suspended ? '' : 'pointer'} d-flex w-100 align-items-center gap-2 px-2 py-3
                                    ${room.receiver_idol_id === receiverIdolId ? 'bg-light' : ''}`}
                                        aria-disabled={room.receiver_idol_is_suspended || undefined}
                                        onClick={room.receiver_idol_is_suspended ? undefined : () => handleRoomClick(room.receiver_idol_id, room.receiver_user_id, room.receiver_idol.uuid)}>
                                        {/* 對方已停權：讀取訊息會回 IDOL_3045，不能點進聊天室（沒有 pointer 就沒有游標與 hover 變色）；
                                            頭像、名稱灰階，頭像右下角疊 ban 標記（標記放在灰階元素外才不會一起變灰），不查在線狀態 */}
                                        <div className="position-relative flex-shrink-0">
                                            <IdolAvatar
                                                size="md"
                                                src={room.receiver_idol.avatar_path && chatImageUrl ?
                                                    `${chatImageUrl}${room.receiver_idol.avatar_path}` :
                                                    placeholder_portrait_55x55}
                                                idolId={room.receiver_idol_is_suspended ? undefined : room.receiver_idol_id}
                                                imgClassName={room.receiver_idol_is_suspended ? 'idol-suspended' : ''}
                                                alt="avatar"
                                            />
                                            {room.receiver_idol_is_suspended && <SuspendedMark variant="icon" />}
                                        </div>
                                        {/* 文字區佔滿頭像與未讀數之間的剩餘寬度，不寫死寬度，未讀數「99+」才不會被擠出列外 */}
                                        <div className="flex-grow-1 min-w-0">
                                            {/* is_official 為 0/1 數字，轉成 bool 避免 IdolName 渲染出「0」 */}
                                            <IdolName name={room.receiver_idol.idol_nick_name} isOfficial={!!room.receiver_idol.is_official} className={room.receiver_idol_is_suspended ? 'idol-suspended' : ''} />
                                            <p className={`single mb-0 text-gray fs-14 ${room.is_read_count > 0 && room.receiver_idol_id !== receiverIdolId ? 'fw-semibold' : ''}`}>
                                                {room.message_type === 2 ? t('chat_room_media_message') : room.message}
                                            </p>
                                            <p className="fs-12 text-gray mb-0">{formatRoomTime(room.created_at)}</p>
                                        </div>
                                        {room.is_read_count > 0 && room.receiver_idol_id !== receiverIdolId ?
                                            <span className="badge rounded-pill bg-primary flex-shrink-0">
                                                {room.is_read_count > 99 ? '99+' : room.is_read_count}
                                            </span>
                                            : ''}
                                    </div>
                                ))
                            ) : (
                                <div className="p-2">
                                    {t('no_data')}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="w-100 position-relative d-flex flex-column" style={{ height: '100%', minWidth: 0 }}>
                        <div className="main-message">
                            <div className="ps-md-3 d-flex align-items-center justify-content-between py-2">
                                <div className="d-flex align-items-center min-w-0">
                                    {/* 還沒選聊天室時只顯示 placeholder，不查在線狀態。
                                        也要確認 receiverIdolId 有值：null 會讓整批 check-online 失敗
                                        （離開聊天室後晚到的 get-messages 已由防亂序丟棄，這裡保留判斷作為防線） */}
                                    <IdolAvatar
                                        size="md"
                                        src={receiverIdolInfo && receiverIdolInfo.avatar_path && chatImageUrl ?
                                            `${chatImageUrl}${receiverIdolInfo.avatar_path}` :
                                            placeholder_portrait_55x55}
                                        idolId={receiverIdolInfo && receiverIdolId !== null && !isReceiverSuspended ? receiverIdolId : undefined}
                                        className="me-3"
                                        alt="avatar"
                                    />
                                    {receiverIdolInfo ? (
                                        <a href={`/idol/${receiverIdolInfo.uuid}`} className="link-none hover-p min-w-0">
                                            <IdolName name={receiverIdolInfo.idol_nick_name} isOfficial={!!receiverIdolInfo.is_official} size="md" />
                                        </a>
                                    ) : (
                                        <h5 className="mb-0">
                                            <span>{t('please_select')}</span>
                                        </h5>
                                    )}
                                </div>
                                {/* 設定按鈕群：統一放在此容器內，位置與右側間距才會一致 */}
                                <div className="d-flex align-items-center flex-shrink-0 gap-2 me-2">
                                    {/* 聊天機器人自動回覆 暫停/恢復（只有該聊天室有設定 webhook 才顯示） */}
                                    {receiverIdolInfo && showWebhookToggle &&
                                        <IconButton size="lg" className="border border-dark"
                                            tooltip={currentRoom.webhook_paused ? t('chatbot_auto_reply_resume') : t('chatbot_auto_reply_pause')}
                                            disabled={webhookToggling}
                                            onClick={handleToggleWebhookPause}>
                                            <FontAwesomeIcon icon={currentRoom.webhook_paused ? faPlay : faPause} />
                                        </IconButton>
                                    }
                                    {receiverIdolInfo && userId !== receiverUserId &&
                                        <Dropdown>
                                            <Dropdown.Toggle as={EllipsisToggle} size="lg" buttonClassName="bg-white" />
                                            <Dropdown.Menu align="end">
                                                <Dropdown.Item as="button" onClick={() => blockConfirmModalRef.current?.show()}>
                                                    <FontAwesomeIcon icon={faBan} className="me-2" />
                                                    {t('block_idol')}
                                                </Dropdown.Item>
                                                <Dropdown.Item as="button" onClick={() => reportModalRef.current?.show(receiverIdolId)}>
                                                    <FontAwesomeIcon icon={faFlag} className="me-2" />
                                                    {t('report', { target: t('idol') })}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    }
                                </div>
                            </div>
                        </div>
                        {/* 聊天記錄 */}
                        <div ref={mainMessageBox} className="main-message-box px-3 pt-2">
                        <div ref={messagesContentRef}>

                            {chatHistory &&
                                (chatHistory.map((message, index) => {
                                    const isTipMessage = message.is_tip_message;
                                    const tipAmount = message.tip_amount;
                                    const isFailed = message.status === 'failed';
                                    const messageKey = message.tempId || `${message.created_at}-${index}`;

                                    const isSelf = message.sender_idol_id === idolId;
                                    const msgText = message.message || '';
                                    const isMediaMessage = message.media?.length > 0;
                                    const link = msgText ? detectLink(msgText) : null;
                                    const isPureUrl = link && msgText.trim() === link.url;

                                    // 跨日分隔線：訊息時間改成只顯示 HH:mm 後，日期資訊由這裡承擔，
                                    // 否則捲到前幾天的訊息會完全看不出是哪一天。
                                    // 沒有 created_at 的樂觀更新訊息（尚在傳送中）不觸發分隔線
                                    const prevMessage = index > 0 ? chatHistory[index - 1] : null;
                                    const isNewDay = !!message.created_at
                                        && (!prevMessage || !isSameLocalDay(prevMessage.created_at, message.created_at));
                                    // 以標籤本身為準：後端若回了非空但不合格式的字串，
                                    // formatChatDayLabel 會回空字串（並 console.error），此時不該渲染出一顆空膠囊
                                    const dayLabel = isNewDay ? formatChatDayLabel(message.created_at) : '';

                                    return (
                                        <React.Fragment key={messageKey}>
                                            {dayLabel && (
                                                <div className="text-center my-3">
                                                    <span className="badge rounded-pill bg-light text-gray fw-normal">
                                                        {dayLabel}
                                                    </span>
                                                </div>
                                            )}
                                            {/* 自己發送 */}
                                            {isSelf ?
                                                <div className="my-2">
                                                <div className="msg-me-wrap ms-auto">
                                                    {/* 文字泡泡：媒體訊息有文字才顯示；純文字訊息按原邏輯 */}
                                                    {isMediaMessage
                                                        ? (msgText && !isPureUrl && (
                                                            <div className="d-flex justify-content-end mb-1">
                                                                <div className="msg-me rounded-4 px-3 py-2 mb-0">
                                                                    <FormattedText text={msgText} className="mb-0" />
                                                                </div>
                                                            </div>
                                                        ))
                                                        : ((!isPureUrl || isFailed) && (
                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                {/* 失敗狀態：警示 icon 在訊息左邊 */}
                                                                {isFailed && (
                                                                    <FontAwesomeIcon
                                                                        icon="fa-solid fa-circle-exclamation"
                                                                        className="text-danger"
                                                                    />
                                                                )}
                                                                <div className={`msg-me rounded-4 px-3 py-2 mb-0 ${isTipMessage ? 'tip-gradient' : ''} ${isFailed ? 'opacity-75' : ''}`}>
                                                                    {isTipMessage && tipAmount && (
                                                                        <div className="mb-1 fw-bold">
                                                                            <span className="me-2">{t('tip_sent')}</span>
                                                                            <span className="badge rounded-pill text-white bg-primary fw-semibold">
                                                                                <FontAwesomeIcon icon="fa-solid fa-donate" className="me-1" />
                                                                                ₣{tipAmount}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <FormattedText text={msgText} className="mb-0" />
                                                                </div>
                                                                {/* 失敗狀態：重傳和刪除按鈕在訊息右邊 */}
                                                                {isFailed && (
                                                                    <div className="d-flex align-items-center">
                                                                        {/* 對方已停權時重傳只會再收到 IDOL_3045，只留刪除 */}
                                                                        {!isReceiverSuspended && (
                                                                            <IconButton tooltip={t('retry_send')} onClick={() => handleRetry(message.tempId)}>
                                                                                <FontAwesomeIcon icon="fa-solid fa-rotate-right" />
                                                                            </IconButton>
                                                                        )}
                                                                        <IconButton tooltip={t('delete')} onClick={() => handleDeleteFailed(message.tempId)}>
                                                                            <FontAwesomeIcon icon="fa-solid fa-trash" />
                                                                        </IconButton>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                    }
                                                    {/* 媒體訊息（無氣泡） */}
                                                    {isMediaMessage && message.media && (
                                                        <div className={`w-100${msgText && !isPureUrl ? ' mt-1' : ''}`}>
                                                            <ChatMediaMessage media={message.media} isSelf={isSelf} />
                                                        </div>
                                                    )}
                                                    {/* 連結預覽（僅文字訊息） */}
                                                    {link && (
                                                        <div className={`d-flex justify-content-end${!isPureUrl ? ' mt-2' : ''}`}>
                                                            <div className="w-100">
                                                                <ChatLinkPreview
                                                                    content={msgText}
                                                                    isSelf={true}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <p className="msg-time px-3 mb-0 ms-auto text-gray fs-12">{formatChatTime(message.created_at)}</p>
                                                </div>
                                            </div>
                                            :
                                            // 對方發送
                                            <div className="my-2">
                                                <div className="d-flex align-items-center">
                                                    <div className="img-sm me-2 flex-shrink-0">
                                                        <SafeImg className="img-fluid rounded-pill"
                                                            src={receiverIdolInfo && receiverIdolInfo.avatar_path && chatImageUrl ?
                                                                `${chatImageUrl}${receiverIdolInfo.avatar_path}` :
                                                                placeholder_portrait_55x55}
                                                            alt="avatar"
                                                        />
                                                    </div>
                                                    <div className="msg-other-wrap flex-grow-1">
                                                        {/* 文字泡泡：媒體訊息有文字才顯示；純文字訊息按原邏輯 */}
                                                        {isMediaMessage
                                                            ? (msgText && !isPureUrl && (
                                                                <div className={`msg-other rounded-4 px-3 py-2 mb-1`}>
                                                                    <FormattedText text={msgText} className="mb-0" />
                                                                </div>
                                                            ))
                                                            : (!isPureUrl && (
                                                                <div className={`msg-other rounded-4 px-3 py-2 mb-0 ${isTipMessage ? 'tip-gradient' : ''}`}>
                                                                    {isTipMessage && tipAmount && (
                                                                        <div className="mb-1 fw-bold">
                                                                            <span className="me-2">{t('tip_sent')}</span>
                                                                            <span className="badge rounded-pill text-white bg-primary fw-semibold">
                                                                                <FontAwesomeIcon icon="fa-solid fa-donate" className="me-1" />
                                                                                ₣{tipAmount}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <FormattedText text={msgText} className="mb-0" />
                                                                </div>
                                                            ))
                                                        }
                                                        {/* 媒體訊息（無氣泡） */}
                                                        {isMediaMessage && message.media && (
                                                            <div className={`w-100${msgText && !isPureUrl ? ' mt-1' : ''}`}>
                                                                <ChatMediaMessage media={message.media} isSelf={isSelf} />
                                                            </div>
                                                        )}
                                                        {/* 連結預覽（僅文字訊息） */}
                                                        {link && (
                                                            <div className={!isPureUrl ? 'mt-2' : ''}>
                                                                <div className="w-100">
                                                                    <ChatLinkPreview
                                                                        content={msgText}
                                                                        isSelf={false}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <p className="msg-time mb-0 me-auto text-gray fs-12">{formatChatTime(message.created_at)}</p>
                                            </div>
                                            }
                                        </React.Fragment>
                                    );
                                })
                                )}
                            {typingIdolId && typingIdolId === receiverIdolId && (
                                <div className="my-2">
                                    <div className="d-flex align-items-center">
                                        <div className="img-sm me-2 flex-shrink-0">
                                            <SafeImg className="img-fluid rounded-pill"
                                                src={receiverIdolInfo && receiverIdolInfo.avatar_path && chatImageUrl ?
                                                    `${chatImageUrl}${receiverIdolInfo.avatar_path}` :
                                                    placeholder_portrait_55x55}
                                                alt="avatar"
                                            />
                                        </div>
                                        <p className="py-2 mb-0 text-gray">{t('is_typing')}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>

                        {receiverIdolId ? (isReceiverSuspended ? (
                            // 對方已停權：不能查看紀錄也不能傳送，輸入區換成提示列
                            <div className="main-msg-bottom w-100">
                                <div className="bg-gray text-white d-flex align-items-center justify-content-center flex-wrap gap-2 p-2">
                                    <FontAwesomeIcon icon={faBan} />
                                    <span>{t('error_idol_3045')}</span>
                                </div>
                            </div>
                        ) : isReceiverUnavailable ? (
                            // 已被對方封鎖（CHAT_5007）：文案刻意中性，不透露封鎖
                            <div className="main-msg-bottom w-100">
                                <div className="bg-gray text-white d-flex align-items-center justify-content-center flex-wrap gap-2 p-2">
                                    <FontAwesomeIcon icon={faBan} />
                                    <span>{t('chat_user_unavailable')}</span>
                                </div>
                            </div>
                        ) : allowMessageStatus === 2 ? (
                            // 未訂閱不可私訊：輸入區換成提示列 + 訂閱按鈕
                            <div className="main-msg-bottom w-100">
                                <div className="bg-gray text-white d-flex align-items-center justify-content-center flex-wrap gap-2 p-2">
                                    <FontAwesomeIcon icon={faLock} />
                                    <span>{t('only_subscribers_can_message')}</span>
                                    <Button variant="primary" size="sm" className="rounded-pill px-3 fw-semibold"
                                        onClick={() => setShowSubscriptionPlanModal(true)}>
                                        {t('subscribe')}
                                    </Button>
                                </div>
                            </div>
                        ) : allowMessageStatus === 3 ? (
                            // 對方關閉聊天不可私訊：輸入區換成提示列，無訂閱按鈕
                            <div className="main-msg-bottom w-100">
                                <div className="bg-gray text-white d-flex align-items-center justify-content-center flex-wrap gap-2 p-2">
                                    <FontAwesomeIcon icon={faBan} />
                                    <span>{t('chat_disabled_cannot_message')}</span>
                                </div>
                            </div>
                        ) : (
                            <Form className="main-msg-bottom w-100">
                                <div className="bg-white px-2 py-3">
                                    <div className="d-flex align-items-end gap-2">
                                        {/* 左側 icon 群：打賞 + 媒體 */}
                                        <div className="d-flex flex-shrink-0">
                                            {tipEnabled && (
                                                <IconButton tooltip={t('donate')} onClick={handleDonate}>
                                                    <FontAwesomeIcon icon={faDonate} />
                                                </IconButton>
                                            )}
                                            <IconButton tooltip={t('upload_media')} onClick={() => attachmentRef.current?.open()}>
                                                <FontAwesomeIcon icon={["fas", "photo-film"]} />
                                            </IconButton>
                                        </div>
                                        {/* 輸入框 */}
                                        <div className="user-input-wrap flex-grow-1" style={{ minWidth: 0 }}>
                                            <ChatMediaAttachment
                                                ref={attachmentRef}
                                                previewFiles={previewFiles}
                                                idolId={idolId}
                                                onFileSelect={(file, mediaType, previewUrl, localId) =>
                                                    setPreviewFiles(prev => [
                                                        ...prev,
                                                        localId
                                                            ? { file: null, mediaType, previewUrl: null, uploading: true, localId }
                                                            : { file, mediaType, previewUrl }
                                                    ])
                                                }
                                                onFileRemove={(index) =>
                                                    setPreviewFiles(prev => {
                                                        const item = prev[index];
                                                        if (item?.videoUrl) URL.revokeObjectURL(item.videoUrl);
                                                        return prev.filter((_, i) => i !== index);
                                                    })
                                                }
                                                onFileUpdate={(localId, updates) =>
                                                    setPreviewFiles(prev => prev.map(item =>
                                                        item.localId === localId ? { ...item, ...updates } : item
                                                    ))
                                                }
                                                onUploadFailed={(localId) =>
                                                    setPreviewFiles(prev => {
                                                        const item = prev.find(p => p.localId === localId);
                                                        if (item?.videoUrl) URL.revokeObjectURL(item.videoUrl);
                                                        return prev.filter(p => p.localId !== localId);
                                                    })
                                                }
                                            />
                                            <Form.Control
                                                ref={textareaRef}
                                                className="user-textarea"
                                                as="textarea"
                                                rows={1}
                                                placeholder={t('start_new_message')}
                                                value={messageText}
                                                autoFocus={!!receiverIdolId}
                                                onChange={(e) => {
                                                    handleMessageInput(e);
                                                    autoResizeTextarea(e);
                                                }}
                                                onKeyDown={(e) => {
                                                    // 組字中按 Enter 是選字，不是送出訊息
                                                    if (e.key === 'Enter' && !e.shiftKey && !isImeComposingKey(e)) {
                                                        e.preventDefault();
                                                        handleSubmit();
                                                    }
                                                }}
                                            />
                                        </div>
                                        {/* 傳送 */}
                                        <IconButton
                                            tooltip={t('send')}
                                            disabled={!canSend}
                                            onClick={handleSubmit}
                                        >
                                            <FontAwesomeIcon icon="fa-solid fa-paper-plane" />
                                        </IconButton>
                                    </div>
                                </div>
                            </Form>
                        )) : ''}
                    </div>
                    {showScrollIcon ?
                        (<FontAwesomeIcon icon="fas fa-arrow-circle-down" className="bottom-icon position-absolute hover-p"
                            onClick={scrollToBottom} />) : ''}
                </div>
            </main>
            <MessageModal ref={errorModal} status="error">
                <p>{t('message_send_failed')}</p>
            </MessageModal>

            {/* 打賞 Modal */}
            <DonateModal
                ref={donateModalRef}
                creatorName={receiverIdolInfo?.idol_nick_name || ''}
                onConfirm={handleDonateConfirm}
            />

            {/* 打賞成功 Modal */}
            <MessageModal ref={modalDonateSuccess} status="success" customIcon={faDonate}>
                <p className="mb-1">{t('donate_success_message')}</p>
                <p className="mb-1 fw-bold break-anywhere">{receiverIdolInfo?.idol_nick_name}</p>
                {donatedAmount && <p className="mb-0 text-primary fs-18 fw-bold">₣{donatedAmount} FCoin</p>}
            </MessageModal>

            {/* 封鎖偶像 Modal */}
            <MessageModal ref={blockModalRef} status="success" onClose={() => navigate('/chats', { replace: true })}>
                <p>{t('idol_blocked')}</p>
            </MessageModal>
            <MessageModal ref={blockConfirmModalRef} status="warning" size="md" locked={isBlocking}>
                <h5 className="mb-5">{t('block_confirm')}</h5>
                <div className="text-end">
                    <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => blockConfirmModalRef.current?.hide()} disabled={isBlocking}>{t('cancel')}</button>
                    <LoadingButton className="btn btn-primary rounded-pill" onClick={blockIdol} isLoading={isBlocking}>{t('confirm')}</LoadingButton>
                </div>
            </MessageModal>

            {/* 舉報偶像 Modal */}
            <ReportModal ref={reportModalRef} targetKey="idol" />

            {/* 訂閱方案選擇 Modal */}
            <SubscriptionPlanModal
                show={showSubscriptionPlanModal}
                onHide={() => setShowSubscriptionPlanModal(false)}
                onConfirm={handleSubscriptionPlanConfirm}
                mode="subscribe"
                idolId={receiverIdolId}
            />

            {/* 訂閱確認 Modal */}
            <SubscriptionConfirmModal
                show={showSubscriptionConfirmModal}
                onHide={() => setShowSubscriptionConfirmModal(false)}
                onConfirm={handleSubscribeConfirm}
                onError={handleSubscribeWalletError}
                mode="subscribe"
                idolName={receiverIdolInfo?.idol_nick_name}
                selectedPlan={selectedSubscriptionPlan}
                isLoading={isSubscribing}
            />

            {/* 訂閱成功 Modal */}
            <MessageModal ref={modalSubscribeSuccess} status="success">
                <p>{t('subscription_success')}</p>
            </MessageModal>

            {/* 狀態碼 Modal */}
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} onClose={unavailableRoomUuid !== null && unavailableRoomUuid === urlIdolUuid ? leaveUnavailableRoom : undefined} />

            {isLoading && <Loading></Loading>}
        </>
    );
}

export default Chats;